import { useState, useEffect, useCallback } from 'react'
import { Plus, ChevronRight, ChevronDown, Edit2, Archive, X, Layers, Copy, ArrowLeft, Grid3x3 } from 'lucide-react'
import { supabase } from './supabase'
import { useToast } from './ToastContext'
import { getServiceArea, getStatus, DONE_STATUSES, SERVICE_AREAS, APPROVAL_PATHS, isActiveProfile } from './constants'
import { format, differenceInDays } from 'date-fns'

const PROJECT_STATUSES = [
  { value: 'planning', label: 'Planning', color: '#94a3b8', bg: '#f1f5f9', icon: '📋' },
  { value: 'active', label: 'Active', color: '#2e7d6e', bg: '#e8f5f2', icon: '▶' },
  { value: 'complete', label: 'Complete', color: '#2e6e3a', bg: '#e8f5eb', icon: '✅' },
  { value: 'archived', label: 'Archived', color: '#94a3b8', bg: '#f1f5f9', icon: '📦' },
]
const getProjectStatus = (v) => PROJECT_STATUSES.find(s => s.value === v)

// ════════════════════════════════════════════════════════════
// Q2 SEO PROJECTS — cross-client status grid
// Read-only grid: clients (rows) × the two Q2 templates (columns).
// Each cell shows the team leader (assignee of the lead step),
// % complete, and an on-track / behind flag based on the due date.
// Reads seo_projects + seo_project_items (NOT the generic projects table).
// ════════════════════════════════════════════════════════════
const Q2_COLUMNS = [
  { templateId: 'tpl-old-blogs-aeo-q2-2026', label: 'Old Blogs - AEO Project', leadStep: 'Create AEO Grid' },
  { templateId: 'tpl-rank-tracker-q2-2026',  label: 'Rank Tracker - Primary Focus Keywords', leadStep: 'Select 5 Focus Keywords' },
]
const Q2_TEMPLATE_IDS = Q2_COLUMNS.map(c => c.templateId)

// Compute % complete + on-track flag for one project from its items
function computeProjectStatus(project) {
  const items = project.items || []
  const total = items.length
  const done = items.filter(i => i.status === 'complete').length
  const pct = total ? Math.round((done / total) * 100) : 0
  const isComplete = project.status === 'complete' || (total > 0 && done === total)

  // Archived = project was closed (finished or not). Show final numbers, no on-track flag.
  if (project.status === 'archived') {
    return { pct, done, total, isComplete, track: 'archived' }
  }

  // On-track heuristic: if not complete and the due date is near/passed,
  // compare elapsed-to-due against completion.
  let track = null // 'on_track' | 'behind' | null (no due date)
  if (!isComplete && project.due_date) {
    const daysToDue = differenceInDays(new Date(project.due_date + 'T12:00:00'), new Date())
    if (daysToDue < 0) track = 'behind'              // past due, not done
    else if (daysToDue <= 7 && pct < 70) track = 'behind'  // due within a week but under 70%
    else track = 'on_track'
  } else if (isComplete) {
    track = 'on_track'
  }
  return { pct, done, total, isComplete, track }
}

function Q2GridCard({ onOpen }) {
  return (
    <div className="card" style={{ cursor: 'pointer', transition: 'box-shadow 0.2s', borderLeft: '4px solid var(--gold)' }}
      onClick={onOpen}
      onMouseEnter={e => e.currentTarget.style.boxShadow = 'var(--shadow-md)'}
      onMouseLeave={e => e.currentTarget.style.boxShadow = 'var(--shadow-sm)'}>
      <div style={{ padding: '16px 20px', display: 'flex', alignItems: 'center', gap: 14 }}>
        <div style={{ fontSize: 26 }}>🔠</div>
        <div style={{ flex: 1 }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 4 }}>
            <h3 style={{ fontFamily: 'var(--font-display)', fontSize: 17, color: 'var(--navy)' }}>Q2 SEO Projects</h3>
            <span className="status-pill" style={{ background: '#fff8e1', color: '#8a6000', fontSize: 11 }}>Status grid</span>
          </div>
          <p style={{ fontSize: 13, color: 'var(--text-secondary)' }}>
            Cross-client view of the two Q2 SEO projects — assignee, % complete, and on-track status per client.
          </p>
        </div>
        <ChevronRight size={18} style={{ color: 'var(--text-muted)' }} />
      </div>
    </div>
  )
}

function Q2GridView({ clients, profiles, onBack, onOpenSeoProject }) {
  const [rows, setRows] = useState([]) // [{ client, byTemplate: { [tplId]: {project, status, leader} } }]
  const [loading, setLoading] = useState(true)
  const [search, setSearch] = useState('')
  const [filterLeader, setFilterLeader] = useState('')
  const [filterStatus, setFilterStatus] = useState('') // '' | 'on_track' | 'behind' | 'complete'

  useEffect(() => {
    let cancelled = false
    const load = async () => {
      const { data: projects } = await supabase
        .from('seo_projects')
        .select('*, items:seo_project_items(*)')
        .in('template_id', Q2_TEMPLATE_IDS)

      if (cancelled) return
      const list = projects || []

      // Group by client_id
      const byClient = {}
      for (const p of list) {
        if (!byClient[p.client_id]) byClient[p.client_id] = {}
        byClient[p.client_id][p.template_id] = p
      }

      const builtRows = Object.keys(byClient).map(clientId => {
        const client = clients.find(c => c.id === clientId)
        const byTemplate = {}
        for (const col of Q2_COLUMNS) {
          const project = byClient[clientId][col.templateId]
          if (!project) { byTemplate[col.templateId] = null; continue }
          const status = computeProjectStatus(project)
          // team leader = assignee of the lead step
          const leadItem = (project.items || []).find(i => (i.topic || '').trim() === col.leadStep)
          const leader = leadItem ? profiles.find(p => p.id === leadItem.assigned_to) : null
          byTemplate[col.templateId] = { project, status, leader }
        }
        return { client, clientId, byTemplate }
      })
      // sort by client name
      builtRows.sort((a, b) => (a.client?.name || '').localeCompare(b.client?.name || ''))

      setRows(builtRows)
      setLoading(false)
    }
    load()
    return () => { cancelled = true }
  }, [clients, profiles])

  const trackPill = (track) => {
    if (track === 'on_track') return <span style={{ fontSize: 10.5, fontWeight: 600, color: '#2e6e3a' }}>● On track</span>
    if (track === 'behind')   return <span style={{ fontSize: 10.5, fontWeight: 600, color: 'var(--red)' }}>● Behind</span>
    if (track === 'archived') return <span style={{ fontSize: 10.5, fontWeight: 600, color: '#94a3b8' }}>📦 Archived</span>
    return null
  }

  // Distinct team leaders present in the grid (for the leader filter dropdown)
  const leaderOptions = []
  const seenLeaders = new Set()
  for (const row of rows) {
    for (const col of Q2_COLUMNS) {
      const cell = row.byTemplate[col.templateId]
      if (cell?.leader && !seenLeaders.has(cell.leader.id)) {
        seenLeaders.add(cell.leader.id)
        leaderOptions.push(cell.leader)
      }
    }
  }
  leaderOptions.sort((a, b) => (a.full_name || '').localeCompare(b.full_name || ''))

  // Does a cell match the leader + status filters?
  const cellMatches = (cell) => {
    if (!cell) return false
    if (filterLeader && cell.leader?.id !== filterLeader) return false
    if (filterStatus === 'complete' && !cell.status.isComplete) return false
    if (filterStatus === 'on_track' && cell.status.track !== 'on_track') return false
    if (filterStatus === 'behind' && cell.status.track !== 'behind') return false
    if (filterStatus === 'archived' && cell.status.track !== 'archived') return false
    return true
  }

  const leaderOrStatusActive = !!filterLeader || !!filterStatus

  // Filter rows: client-name search, plus (if leader/status set) at least one matching cell
  const visibleRows = rows.filter(row => {
    if (search && !(row.client?.name || '').toLowerCase().includes(search.toLowerCase())) return false
    if (leaderOrStatusActive) {
      const anyMatch = Q2_COLUMNS.some(col => cellMatches(row.byTemplate[col.templateId]))
      if (!anyMatch) return false
    }
    return true
  })

  const clearFilters = () => { setSearch(''); setFilterLeader(''); setFilterStatus('') }
  const anyFilterActive = !!search || leaderOrStatusActive

  return (
    <div>
      <div style={{ display: 'flex', alignItems: 'center', gap: 12, marginBottom: 20 }}>
        <button className="btn btn-ghost btn-sm" onClick={onBack}><ArrowLeft size={14} /> Back</button>
        <div style={{ flex: 1 }}>
          <h1 style={{ fontFamily: 'var(--font-display)', fontSize: 24, color: 'var(--navy)' }}>Q2 SEO Projects</h1>
          <p style={{ color: 'var(--text-secondary)', fontSize: 13 }}>Status of each client's Q2 SEO projects. Read-only overview.</p>
        </div>
      </div>

      {/* Filters */}
      {!loading && rows.length > 0 && (
        <div className="filters-bar" style={{ marginBottom: 16 }}>
          <input
            className="search-input"
            placeholder="Search clients..."
            value={search}
            onChange={e => setSearch(e.target.value)}
          />
          <select className="filter-select" value={filterLeader} onChange={e => setFilterLeader(e.target.value)}>
            <option value="">All team leaders</option>
            {leaderOptions.map(l => <option key={l.id} value={l.id}>{l.full_name}</option>)}
          </select>
          <select className="filter-select" value={filterStatus} onChange={e => setFilterStatus(e.target.value)}>
            <option value="">All statuses</option>
            <option value="on_track">On track</option>
            <option value="behind">Behind</option>
            <option value="complete">Complete</option>
            <option value="archived">Archived</option>
          </select>
          {anyFilterActive && (
            <button className="btn btn-ghost btn-sm" onClick={clearFilters}>Clear filters</button>
          )}
          <div style={{ marginLeft: 'auto' }}>
            <span style={{ fontSize: 12, color: 'var(--text-muted)' }}>
              {visibleRows.length} client{visibleRows.length !== 1 ? 's' : ''}
            </span>
          </div>
        </div>
      )}

      {loading ? (
        <div style={{ textAlign: 'center', padding: 40, color: 'var(--text-muted)' }}>Loading...</div>
      ) : rows.length === 0 ? (
        <div className="empty-state">
          <Grid3x3 size={48} className="empty-state-icon" />
          <h3>No Q2 SEO projects yet</h3>
          <p>Once Q2 projects are seeded for clients, they'll appear here.</p>
        </div>
      ) : visibleRows.length === 0 ? (
        <div className="empty-state">
          <Grid3x3 size={48} className="empty-state-icon" />
          <h3>No clients match your filters</h3>
          <p>Try clearing the search or filters.</p>
        </div>
      ) : (
        <div className="card" style={{ overflow: 'auto' }}>
          <table style={{ width: '100%', borderCollapse: 'collapse', minWidth: 640 }}>
            <thead>
              <tr>
                <th style={{ textAlign: 'left', padding: '12px 16px', fontSize: 11, textTransform: 'uppercase', letterSpacing: '0.8px', color: 'var(--text-muted)', borderBottom: '1px solid var(--border)', background: 'var(--cream)', minWidth: 160, position: 'sticky', left: 0, zIndex: 1 }}>
                  Client
                </th>
                {Q2_COLUMNS.map(col => (
                  <th key={col.templateId} style={{ padding: '12px 14px', fontSize: 11, textTransform: 'uppercase', letterSpacing: '0.5px', color: 'var(--text-muted)', borderBottom: '1px solid var(--border)', background: 'var(--cream)', textAlign: 'left', minWidth: 220 }}>
                    {col.label}
                  </th>
                ))}
              </tr>
            </thead>
            <tbody>
              {visibleRows.map(row => (
                <tr key={row.clientId}>
                  <td style={{ padding: '12px 16px', fontSize: 13.5, fontWeight: 600, color: 'var(--navy)', borderBottom: '1px solid var(--border-light)', position: 'sticky', left: 0, background: '#fff', zIndex: 1 }}>
                    {row.client?.name || '(unknown client)'}
                  </td>
                  {Q2_COLUMNS.map(col => {
                    const cell = row.byTemplate[col.templateId]
                    // dim cells that don't match an active leader/status filter
                    const dim = leaderOrStatusActive && cell && !cellMatches(cell)
                    const clickable = !!cell && !!onOpenSeoProject
                    return (
                      <td key={col.templateId}
                        onClick={clickable ? () => onOpenSeoProject(row.clientId, cell.project.id) : undefined}
                        style={{
                          padding: '10px 14px', borderBottom: '1px solid var(--border-light)', verticalAlign: 'top',
                          cursor: clickable ? 'pointer' : 'default', opacity: dim ? 0.35 : 1,
                          transition: 'background 0.12s',
                        }}
                        onMouseEnter={clickable ? e => e.currentTarget.style.background = 'var(--cream)' : undefined}
                        onMouseLeave={clickable ? e => e.currentTarget.style.background = 'transparent' : undefined}
                        title={clickable ? 'Open this project in the SEO workspace' : undefined}
                      >
                        {!cell ? (
                          <span style={{ color: 'var(--border)', fontSize: 16 }}>—</span>
                        ) : (
                          <div>
                            <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 5 }}>
                              <div className="progress-bar-wrap" style={{ flex: 1, maxWidth: 110, height: 7 }}>
                                <div className="progress-bar-fill" style={{ width: cell.status.pct + '%' }} />
                              </div>
                              <span style={{ fontSize: 12.5, fontWeight: 700, color: cell.status.isComplete ? '#2e6e3a' : 'var(--navy)' }}>
                                {cell.status.pct}%
                              </span>
                            </div>
                            <div style={{ fontSize: 11.5, color: 'var(--text-secondary)' }}>
                              {cell.leader ? cell.leader.full_name : <span style={{ color: 'var(--text-muted)', fontStyle: 'italic' }}>Unassigned</span>}
                            </div>
                            <div style={{ fontSize: 11, color: 'var(--text-muted)', marginTop: 2 }}>
                              {cell.status.done}/{cell.status.total} steps {trackPill(cell.status.track) && <>· {trackPill(cell.status.track)}</>}
                            </div>
                          </div>
                        )}
                      </td>
                    )
                  })}
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  )
}

// ─── Project Modal ────────────────────────────────────────────────────────────
function ProjectModal({ project, clients, profiles, currentUser, onClose, onSaved }) {
  const toast = useToast()
  const isEdit = !!project?.id
  const [form, setForm] = useState({
    name: project?.name || '',
    description: project?.description || '',
    status: project?.status || 'planning',
    owner_id: project?.owner_id || currentUser.id,
    client_ids: project?.client_ids || [],
    checkin_interval_days: project?.checkin_interval_days || 7,
  })
  const [saving, setSaving] = useState(false)
  const set = (k, v) => setForm(f => ({ ...f, [k]: v }))
  const toggleClient = (id) => set('client_ids', form.client_ids.includes(id) ? form.client_ids.filter(c => c !== id) : [...form.client_ids, id])

  const save = async () => {
    if (!form.name.trim()) { toast('Project name required', 'error'); return }
    setSaving(true)
    try {
      let error
      if (isEdit) { ;({ error } = await supabase.from('projects').update(form).eq('id', project.id)) }
      else { ;({ error } = await supabase.from('projects').insert(form)) }
      if (error) throw error
      toast(isEdit ? 'Project updated' : 'Project created', 'success')
      onSaved()
    } catch (e) { toast('Error: ' + e.message, 'error') }
    finally { setSaving(false) }
  }

  return (
    <div className="modal-overlay" onClick={e => e.target === e.currentTarget && onClose()}>
      <div className="modal" style={{ maxWidth: 600 }}>
        <div className="modal-header">
          <span className="modal-title">{isEdit ? 'Edit Project' : 'New Project'}</span>
          <button className="btn-icon" onClick={onClose}><X size={16} /></button>
        </div>
        <div className="modal-body">
          <div className="form-group">
            <label className="form-label">Project Name *</label>
            <input className="form-control" value={form.name} onChange={e => set('name', e.target.value)} placeholder="e.g. Q3 Blog Refresh" autoFocus />
          </div>
          <div className="form-group">
            <label className="form-label">Description</label>
            <textarea className="form-control" rows={2} value={form.description} onChange={e => set('description', e.target.value)} placeholder="What is this project about?" />
          </div>
          <div className="form-row">
            <div className="form-group">
              <label className="form-label">Status</label>
              <select className="form-control" value={form.status} onChange={e => set('status', e.target.value)}>
                {PROJECT_STATUSES.filter(s => s.value !== 'archived').map(s => <option key={s.value} value={s.value}>{s.icon} {s.label}</option>)}
              </select>
            </div>
            <div className="form-group">
              <label className="form-label">Owner</label>
              <select className="form-control" value={form.owner_id} onChange={e => set('owner_id', e.target.value)}>
                {profiles.filter(isActiveProfile).map(p => <option key={p.id} value={p.id}>{p.full_name}</option>)}
              </select>
            </div>
            <div className="form-group">
              <label className="form-label">Check-in Reminder</label>
              <select className="form-control" value={form.checkin_interval_days} onChange={e => set('checkin_interval_days', parseInt(e.target.value))}>
                <option value={3}>Every 3 days</option>
                <option value={7}>Every 7 days</option>
                <option value={14}>Every 14 days</option>
                <option value={30}>Monthly</option>
                <option value={0}>No reminder</option>
              </select>
            </div>
          </div>
          <div className="form-group">
            <label className="form-label">Clients Involved</label>
            <div style={{ display: 'flex', flexWrap: 'wrap', gap: 8, marginTop: 4 }}>
              {clients.map(c => {
                const selected = form.client_ids.includes(c.id)
                return (
                  <button key={c.id} onClick={() => toggleClient(c.id)} style={{
                    padding: '5px 12px', borderRadius: 20, fontSize: 13, cursor: 'pointer', border: '1.5px solid',
                    background: selected ? 'var(--navy-mid)' : '#fff', color: selected ? '#fff' : 'var(--text-secondary)',
                    borderColor: selected ? 'var(--navy-mid)' : 'var(--border)', fontWeight: selected ? 600 : 400,
                  }}>{c.name}</button>
                )
              })}
            </div>
            <div style={{ fontSize: 12, color: 'var(--text-muted)', marginTop: 6 }}>
              {form.client_ids.length === 0 ? 'No clients selected — tasks can still be added as internal/general' : `${form.client_ids.length} client${form.client_ids.length !== 1 ? 's' : ''} selected`}
            </div>
          </div>
        </div>
        <div className="modal-footer">
          <button className="btn btn-ghost" onClick={onClose}>Cancel</button>
          <button className="btn btn-gold" onClick={save} disabled={saving}>{saving ? 'Saving...' : isEdit ? 'Save Changes' : 'Create Project'}</button>
        </div>
      </div>
    </div>
  )
}

// ─── Add Phase Modal ──────────────────────────────────────────────────────────
function AddPhaseModal({ projectId, existingCount, onClose, onSaved }) {
  const toast = useToast()
  const [name, setName] = useState('')
  const [saving, setSaving] = useState(false)
  const save = async () => {
    if (!name.trim()) return
    setSaving(true)
    const { error } = await supabase.from('project_phases').insert({ project_id: projectId, name: name.trim(), order_index: existingCount })
    if (error) { toast('Error: ' + error.message, 'error'); setSaving(false); return }
    toast('Phase added', 'success'); onSaved()
  }
  return (
    <div className="modal-overlay" onClick={e => e.target === e.currentTarget && onClose()}>
      <div className="modal" style={{ maxWidth: 380 }}>
        <div className="modal-header"><span className="modal-title">Add Phase</span><button className="btn-icon" onClick={onClose}><X size={16} /></button></div>
        <div className="modal-body">
          <div className="form-group">
            <label className="form-label">Phase Name</label>
            <input className="form-control" value={name} onChange={e => setName(e.target.value)} placeholder="e.g. Discovery, Build, Launch..." autoFocus onKeyDown={e => e.key === 'Enter' && save()} />
          </div>
        </div>
        <div className="modal-footer">
          <button className="btn btn-ghost" onClick={onClose}>Cancel</button>
          <button className="btn btn-gold" onClick={save} disabled={saving || !name.trim()}>{saving ? 'Adding...' : 'Add Phase'}</button>
        </div>
      </div>
    </div>
  )
}

// ─── Add Task Modal (single or bulk) ─────────────────────────────────────────
function AddTaskModal({ project, phase, phases, clients, profiles, currentUser, onClose, onSaved }) {
  const toast = useToast()
  const projectClients = (project.client_ids || []).map(id => clients.find(c => c.id === id)).filter(Boolean)
  const [mode, setMode] = useState('single') // single | bulk
  const [bulkClientIds, setBulkClientIds] = useState(projectClients.map(c => c.id))
  const [form, setForm] = useState({
    title: '', service_area: 'website', assigned_to: '',
    date_due: '', week_number: '', approval_path: 'internal',
    project_phase_id: phase?.id || '',
    status: 'not_started', client_visible: true, is_onboarding_task: false,
    client_id: '', // empty = internal/general
  })
  const [saving, setSaving] = useState(false)
  const set = (k, v) => setForm(f => ({ ...f, [k]: v }))

  const toggleBulkClient = (id) => setBulkClientIds(prev => prev.includes(id) ? prev.filter(c => c !== id) : [...prev, id])

  const save = async () => {
    if (!form.title.trim()) { toast('Title required', 'error'); return }
    setSaving(true)
    try {
      const base = {
        title: form.title.trim(),
        service_area: form.service_area,
        assigned_to: form.assigned_to || null,
        assigned_by: currentUser.id,
        date_due: form.date_due || null,
        week_number: form.week_number ? parseInt(form.week_number) : null,
        approval_path: form.approval_path,
        project_phase_id: form.project_phase_id || null,
        project_id: project.id,
        status: 'not_started',
        client_visible: form.client_visible,
        is_onboarding_task: form.is_onboarding_task,
      }

      if (mode === 'bulk' && bulkClientIds.length > 0) {
        // Create one task per selected client
        const inserts = bulkClientIds.map(cid => ({ ...base, client_id: cid }))
        const { data: tasks, error } = await supabase.from('tasks').insert(inserts).select()
        if (error) throw error
        for (const t of tasks || []) {
          supabase.functions.invoke('sync-tasks-to-sheets', { body: { task_id: t.id } })
        }
        toast(`Created ${tasks?.length} tasks across ${bulkClientIds.length} clients`, 'success')
      } else {
        // Single task — client optional
        const { data: task, error } = await supabase.from('tasks').insert({
          ...base,
          client_id: form.client_id || null,
        }).select().single()
        if (error) throw error
        if (task?.id) supabase.functions.invoke('sync-tasks-to-sheets', { body: { task_id: task.id } })
        toast('Task added', 'success')
      }
      onSaved()
    } catch (e) { toast('Error: ' + e.message, 'error') }
    finally { setSaving(false) }
  }

  return (
    <div className="modal-overlay" onClick={e => e.target === e.currentTarget && onClose()}>
      <div className="modal" style={{ maxWidth: 600 }}>
        <div className="modal-header">
          <span className="modal-title">Add Task to Project</span>
          <button className="btn-icon" onClick={onClose}><X size={16} /></button>
        </div>
        <div className="modal-body">
          {/* Mode toggle */}
          {projectClients.length > 1 && (
            <div className="tabs" style={{ marginBottom: 16 }}>
              <button className={`tab ${mode === 'single' ? 'active' : ''}`} onClick={() => setMode('single')}>Single Task</button>
              <button className={`tab ${mode === 'bulk' ? 'active' : ''}`} onClick={() => setMode('bulk')}>
                Apply to Multiple Clients
              </button>
            </div>
          )}

          <div className="form-group">
            <label className="form-label">Task Title *</label>
            <input className="form-control" value={form.title} onChange={e => set('title', e.target.value)} placeholder="What needs to be done?" autoFocus />
          </div>

          {mode === 'single' ? (
            <div className="form-group">
              <label className="form-label">Client <span style={{ color: 'var(--text-muted)', fontWeight: 400 }}>(optional — leave blank for internal tasks)</span></label>
              <select className="form-control" value={form.client_id} onChange={e => set('client_id', e.target.value)}>
                <option value="">🏢 Internal / General (no client)</option>
                {projectClients.length > 0
                  ? projectClients.map(c => <option key={c.id} value={c.id}>{c.name}</option>)
                  : clients.map(c => <option key={c.id} value={c.id}>{c.name}</option>)
                }
              </select>
            </div>
          ) : (
            <div className="form-group">
              <label className="form-label">Apply to these clients</label>
              <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap', marginTop: 4 }}>
                <button onClick={() => setBulkClientIds(projectClients.map(c => c.id))}
                  className="btn btn-ghost btn-sm">All</button>
                <button onClick={() => setBulkClientIds([])}
                  className="btn btn-ghost btn-sm">None</button>
                {projectClients.map(c => {
                  const sel = bulkClientIds.includes(c.id)
                  return (
                    <button key={c.id} onClick={() => toggleBulkClient(c.id)} style={{
                      padding: '4px 12px', borderRadius: 20, fontSize: 13, cursor: 'pointer', border: '1.5px solid',
                      background: sel ? 'var(--teal)' : '#fff', color: sel ? '#fff' : 'var(--text-secondary)',
                      borderColor: sel ? 'var(--teal)' : 'var(--border)', fontWeight: sel ? 600 : 400,
                    }}>{c.name}</button>
                  )
                })}
              </div>
              {bulkClientIds.length > 0 && (
                <div style={{ fontSize: 12, color: 'var(--teal)', marginTop: 6, fontWeight: 500 }}>
                  Will create {bulkClientIds.length} task{bulkClientIds.length !== 1 ? 's' : ''} — one per client
                </div>
              )}
            </div>
          )}

          <div className="form-row">
            <div className="form-group">
              <label className="form-label">Phase</label>
              <select className="form-control" value={form.project_phase_id} onChange={e => set('project_phase_id', e.target.value)}>
                <option value="">No phase</option>
                {phases.map(p => <option key={p.id} value={p.id}>{p.name}</option>)}
              </select>
            </div>
            <div className="form-group">
              <label className="form-label">Week #</label>
              <input className="form-control" type="number" min="1" value={form.week_number} onChange={e => set('week_number', e.target.value)} placeholder="e.g. 1" />
            </div>
          </div>

          <div className="form-row">
            <div className="form-group">
              <label className="form-label">Assigned To</label>
              <select className="form-control" value={form.assigned_to} onChange={e => set('assigned_to', e.target.value)}>
                <option value="">Unassigned</option>
                {profiles.filter(isActiveProfile).map(p => <option key={p.id} value={p.id}>{p.full_name}</option>)}
              </select>
            </div>
            <div className="form-group">
              <label className="form-label">Service Area</label>
              <select className="form-control" value={form.service_area} onChange={e => set('service_area', e.target.value)}>
                {SERVICE_AREAS.map(s => <option key={s.value} value={s.value}>{s.label}</option>)}
              </select>
            </div>
            <div className="form-group">
              <label className="form-label">Due Date</label>
              <input className="form-control" type="date" value={form.date_due} onChange={e => set('date_due', e.target.value)} />
            </div>
          </div>

          <div className="form-group">
            <label className="form-label">Approval Path</label>
            <select className="form-control" value={form.approval_path} onChange={e => set('approval_path', e.target.value)}>
              {APPROVAL_PATHS.map(a => <option key={a.value} value={a.value}>{a.label}</option>)}
            </select>
          </div>

          <div style={{ display: 'flex', gap: 20 }}>
            <label style={{ display: 'flex', alignItems: 'center', gap: 6, cursor: 'pointer', fontSize: 13 }}>
              <input type="checkbox" checked={form.is_onboarding_task} onChange={e => set('is_onboarding_task', e.target.checked)} />
              Onboarding task
            </label>
            <label style={{ display: 'flex', alignItems: 'center', gap: 6, cursor: 'pointer', fontSize: 13 }}>
              <input type="checkbox" checked={!form.client_visible} onChange={e => set('client_visible', !e.target.checked)} />
              Hide from client dashboard
            </label>
          </div>
        </div>
        <div className="modal-footer">
          <button className="btn btn-ghost" onClick={onClose}>Cancel</button>
          <button className="btn btn-gold" onClick={save} disabled={saving || (mode === 'bulk' && bulkClientIds.length === 0)}>
            {saving ? 'Creating...' : mode === 'bulk' ? `Create ${bulkClientIds.length} Tasks` : 'Add Task'}
          </button>
        </div>
      </div>
    </div>
  )
}

// ─── Task row in project ──────────────────────────────────────────────────────
function ProjectTaskRow({ task, clients, profiles, onTaskClick }) {
  const st = getStatus(task.status)
  const sa = getServiceArea(task.service_area)
  const client = clients.find(c => c.id === task.client_id)
  const assignee = profiles.find(p => p.id === task.assigned_to)
  const isOverdue = task.date_due && new Date(task.date_due + 'T12:00:00') < new Date() && !DONE_STATUSES.includes(task.status)

  return (
    <tr onClick={() => onTaskClick && onTaskClick(task)} style={{ cursor: onTaskClick ? 'pointer' : 'default' }}>
      <td style={{ width: '35%' }}>
        <div style={{ fontWeight: 500, fontSize: 13.5 }}>{task.title}</div>
        {sa && <span className="badge" style={{ background: sa.color + '18', color: sa.color, fontSize: 11, marginTop: 2 }}>
          <span className="service-dot" style={{ background: sa.color }} />{sa.label}
        </span>}
      </td>
      <td>
        <span style={{ fontSize: 13, color: client ? 'var(--text-secondary)' : 'var(--text-muted)', fontStyle: client ? 'normal' : 'italic' }}>
          {client?.name || '🏢 Internal'}
        </span>
      </td>
      <td><span style={{ fontSize: 13 }}>{assignee?.full_name || <span style={{ color: 'var(--text-muted)' }}>—</span>}</span></td>
      <td>
        <span style={{ fontSize: 13, color: isOverdue ? 'var(--urgent)' : 'var(--text-secondary)', fontWeight: isOverdue ? 600 : 400 }}>
          {task.date_due ? format(new Date(task.date_due + 'T12:00:00'), 'MMM d') : '—'}
        </span>
      </td>
      <td>{st && <span className="status-pill" style={{ background: st.bg, color: st.color }}>{st.icon} {st.label}</span>}</td>
    </tr>
  )
}

// ─── Bulk status matrix (task x client) ──────────────────────────────────────
function BulkStatusMatrix({ tasks, clients, projectClientIds }) {
  const projectClients = projectClientIds.map(id => clients.find(c => c.id === id)).filter(Boolean)
  if (projectClients.length === 0) return null

  // Group tasks by title to find cross-client tasks
  const taskGroups = {}
  tasks.forEach(t => {
    if (!t.client_id) return
    const key = t.title
    if (!taskGroups[key]) taskGroups[key] = {}
    taskGroups[key][t.client_id] = t
  })

  const groupEntries = Object.entries(taskGroups).filter(([, byClient]) => Object.keys(byClient).length > 1)
  if (groupEntries.length === 0) return null

  return (
    <div className="card" style={{ overflow: 'auto', marginBottom: 20 }}>
      <div className="card-header" style={{ background: 'var(--navy)' }}>
        <span style={{ fontFamily: 'var(--font-display)', fontSize: 15, color: '#fff' }}>📊 Cross-Client Progress</span>
        <span style={{ fontSize: 12, color: 'rgba(255,255,255,0.5)' }}>Tasks applied to multiple clients</span>
      </div>
      <div style={{ overflowX: 'auto' }}>
        <table style={{ width: '100%', borderCollapse: 'collapse', minWidth: 500 }}>
          <thead>
            <tr>
              <th style={{ textAlign: 'left', padding: '10px 14px', fontSize: 11, textTransform: 'uppercase', letterSpacing: '0.8px', color: 'var(--text-muted)', borderBottom: '1px solid var(--border)', background: 'var(--cream)', minWidth: 180 }}>Task</th>
              {projectClients.map(c => (
                <th key={c.id} style={{ padding: '10px 10px', fontSize: 11, textTransform: 'uppercase', letterSpacing: '0.5px', color: 'var(--text-muted)', borderBottom: '1px solid var(--border)', background: 'var(--cream)', textAlign: 'center', minWidth: 110, whiteSpace: 'nowrap' }}>
                  {c.name}
                </th>
              ))}
              <th style={{ padding: '10px 10px', fontSize: 11, textTransform: 'uppercase', letterSpacing: '0.5px', color: 'var(--text-muted)', borderBottom: '1px solid var(--border)', background: 'var(--cream)', textAlign: 'center', minWidth: 70 }}>Done</th>
            </tr>
          </thead>
          <tbody>
            {groupEntries.map(([title, byClient]) => {
              const doneCount = Object.values(byClient).filter(t => DONE_STATUSES.includes(t.status)).length
              const totalCount = Object.keys(byClient).length
              return (
                <tr key={title}>
                  <td style={{ padding: '10px 14px', fontSize: 13.5, fontWeight: 500, borderBottom: '1px solid var(--border-light)' }}>{title}</td>
                  {projectClients.map(c => {
                    const t = byClient[c.id]
                    const st = t ? getStatus(t.status) : null
                    return (
                      <td key={c.id} style={{ padding: '8px 10px', textAlign: 'center', borderBottom: '1px solid var(--border-light)' }}>
                        {t ? (
                          <span style={{ display: 'inline-flex', alignItems: 'center', gap: 3, padding: '3px 8px', borderRadius: 20, fontSize: 11, fontWeight: 500, background: st?.bg, color: st?.color }}>
                            {st?.icon} {st?.label}
                          </span>
                        ) : (
                          <span style={{ color: 'var(--border)', fontSize: 16 }}>—</span>
                        )}
                      </td>
                    )
                  })}
                  <td style={{ padding: '8px 10px', textAlign: 'center', borderBottom: '1px solid var(--border-light)' }}>
                    <span style={{ fontWeight: 700, color: doneCount === totalCount ? 'var(--teal)' : 'var(--text-secondary)', fontSize: 13 }}>
                      {doneCount}/{totalCount}
                    </span>
                  </td>
                </tr>
              )
            })}
          </tbody>
        </table>
      </div>
    </div>
  )
}

// ─── Project Detail ───────────────────────────────────────────────────────────
function ProjectDetail({ project, clients, profiles, currentUser, onBack, onEdited, onTaskClick }) {
  const toast = useToast()
  const [phases, setPhases] = useState([])
  const [tasks, setTasks] = useState([])
  const [expandedPhases, setExpandedPhases] = useState({})
  const [showAddPhase, setShowAddPhase] = useState(false)
  const [showAddTask, setShowAddTask] = useState(false)
  const [addTaskPhase, setAddTaskPhase] = useState(null)
  const [showEditProject, setShowEditProject] = useState(false)
  const [checkinDays, setCheckinDays] = useState(null)
  const [activeClientFilter, setActiveClientFilter] = useState(null) // null = all
  const [viewMode, setViewMode] = useState('phase') // phase | client

  const projectClients = (project.client_ids || []).map(id => clients.find(c => c.id === id)).filter(Boolean)

  const load = useCallback(async () => {
    const [{ data: ph }, { data: ts }] = await Promise.all([
      supabase.from('project_phases').select('*').eq('project_id', project.id).order('order_index'),
      supabase.from('tasks').select('*, client:clients(name), assignee:profiles!tasks_assigned_to_fkey(full_name)').eq('project_id', project.id).order('created_at'),
    ])
    setPhases(ph || [])
    setTasks(ts || [])
    const expanded = {}
    ;(ph || []).forEach(p => { expanded[p.id] = true })
    setExpandedPhases(expanded)

    if (project.last_checked_in_at && project.checkin_interval_days > 0) {
      const days = differenceInDays(new Date(), new Date(project.last_checked_in_at))
      if (days >= project.checkin_interval_days) setCheckinDays(days)
    } else if (!project.last_checked_in_at && project.checkin_interval_days > 0) {
      setCheckinDays('never')
    }
  }, [project.id, project.last_checked_in_at, project.checkin_interval_days])

  useEffect(() => { load() }, [load])

  const markCheckedIn = async () => {
    await supabase.from('projects').update({ last_checked_in_at: new Date().toISOString() }).eq('id', project.id)
    setCheckinDays(null)
    toast('Check-in recorded ✓', 'success')
  }

  const archiveProject = async () => {
    if (!window.confirm('Archive this project?')) return
    await supabase.from('projects').update({ status: 'archived' }).eq('id', project.id)
    toast('Project archived', 'success')
    onBack()
  }

  const filteredTasks = activeClientFilter === 'internal'
    ? tasks.filter(t => !t.client_id)
    : activeClientFilter
      ? tasks.filter(t => t.client_id === activeClientFilter)
      : tasks

  const totalTasks = tasks.length
  const doneTasks = tasks.filter(t => DONE_STATUSES.includes(t.status)).length
  const pct = totalTasks ? Math.round((doneTasks / totalTasks) * 100) : 0
  const blockedCount = tasks.filter(t => t.status === 'blocked').length
  const ps = getProjectStatus(project.status)
  const unassignedTasks = filteredTasks.filter(t => !t.project_phase_id)
  const internalTasks = tasks.filter(t => !t.client_id)

  const renderTaskTable = (rows) => (
    <table className="task-table" style={{ width: '100%' }}>
      <thead><tr><th>Task</th><th>Client</th><th>Assigned To</th><th>Due</th><th>Status</th></tr></thead>
      <tbody>
        {rows.length === 0
          ? <tr><td colSpan={5} style={{ textAlign: 'center', color: 'var(--text-muted)', padding: 20, fontStyle: 'italic' }}>No tasks</td></tr>
          : rows.map(t => <ProjectTaskRow key={t.id} task={t} clients={clients} profiles={profiles} onTaskClick={onTaskClick} />)
        }
      </tbody>
    </table>
  )

  return (
    <div>
      {/* Header */}
      <div style={{ display: 'flex', alignItems: 'flex-start', gap: 12, marginBottom: 20 }}>
        <button className="btn btn-ghost btn-sm" onClick={onBack}>← Back</button>
        <div style={{ flex: 1 }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: 4 }}>
            <h1 style={{ fontFamily: 'var(--font-display)', fontSize: 24, color: 'var(--navy)' }}>{project.name}</h1>
            {ps && <span className="status-pill" style={{ background: ps.bg, color: ps.color }}>{ps.icon} {ps.label}</span>}
          </div>
          {project.description && <p style={{ color: 'var(--text-secondary)', fontSize: 13.5, marginBottom: 8 }}>{project.description}</p>}
        </div>
        <div style={{ display: 'flex', gap: 8 }}>
          <button className="btn btn-ghost btn-sm" onClick={() => setShowEditProject(true)}><Edit2 size={13} /> Edit</button>
          <button className="btn btn-ghost btn-sm" onClick={archiveProject}><Archive size={13} /> Archive</button>
          <button className="btn btn-gold btn-sm" onClick={() => { setAddTaskPhase(null); setShowAddTask(true) }}>
            <Plus size={13} /> Add Task
          </button>
        </div>
      </div>

      {/* Check-in banner */}
      {checkinDays && (
        <div style={{ background: '#fdf6e3', border: '1.5px solid var(--gold)', borderRadius: 10, padding: '12px 16px', marginBottom: 16, display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
          <span style={{ fontSize: 13.5, color: 'var(--gold)', fontWeight: 500 }}>
            ⏰ {checkinDays === 'never' ? "You haven't checked in on this project yet" : `Last check-in was ${checkinDays} days ago`}
          </span>
          <button className="btn btn-gold btn-sm" onClick={markCheckedIn}>Mark Checked In</button>
        </div>
      )}

      {/* Progress */}
      <div className="card card-body" style={{ marginBottom: 20 }}>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 8 }}>
          <span style={{ fontWeight: 600, fontSize: 14 }}>{doneTasks} of {totalTasks} tasks complete</span>
          <span style={{ fontFamily: 'var(--font-display)', fontSize: 22, color: 'var(--teal)' }}>{pct}%</span>
        </div>
        <div className="progress-bar-wrap"><div className="progress-bar-fill" style={{ width: pct + '%' }} /></div>
        <div style={{ display: 'flex', gap: 20, marginTop: 12 }}>
          <div><span style={{ fontFamily: 'var(--font-display)', fontSize: 20, color: 'var(--navy)' }}>{totalTasks}</span><div style={{ fontSize: 11, color: 'var(--text-muted)' }}>Total</div></div>
          <div><span style={{ fontFamily: 'var(--font-display)', fontSize: 20, color: 'var(--teal)' }}>{doneTasks}</span><div style={{ fontSize: 11, color: 'var(--text-muted)' }}>Done</div></div>
          <div><span style={{ fontFamily: 'var(--font-display)', fontSize: 20, color: 'var(--gold)' }}>{tasks.filter(t => t.status === 'in_progress').length}</span><div style={{ fontSize: 11, color: 'var(--text-muted)' }}>In Progress</div></div>
          {blockedCount > 0 && <div><span style={{ fontFamily: 'var(--font-display)', fontSize: 20, color: 'var(--red)' }}>{blockedCount}</span><div style={{ fontSize: 11, color: 'var(--text-muted)' }}>Blocked</div></div>}
        </div>
      </div>

      {/* Client filter tabs */}
      {projectClients.length > 0 && (
        <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap', marginBottom: 16, alignItems: 'center' }}>
          <span style={{ fontSize: 12, color: 'var(--text-muted)', marginRight: 4 }}>Filter by:</span>
          <button onClick={() => setActiveClientFilter(null)}
            style={{ padding: '4px 12px', borderRadius: 20, fontSize: 12, cursor: 'pointer', border: '1.5px solid', fontWeight: activeClientFilter === null ? 700 : 400, background: activeClientFilter === null ? 'var(--navy-mid)' : '#fff', color: activeClientFilter === null ? '#fff' : 'var(--text-secondary)', borderColor: activeClientFilter === null ? 'var(--navy-mid)' : 'var(--border)' }}>
            All ({tasks.length})
          </button>
          {projectClients.map(c => {
            const count = tasks.filter(t => t.client_id === c.id).length
            const done = tasks.filter(t => t.client_id === c.id && DONE_STATUSES.includes(t.status)).length
            return (
              <button key={c.id} onClick={() => setActiveClientFilter(c.id)}
                style={{ padding: '4px 12px', borderRadius: 20, fontSize: 12, cursor: 'pointer', border: '1.5px solid', fontWeight: activeClientFilter === c.id ? 700 : 400, background: activeClientFilter === c.id ? 'var(--navy-mid)' : '#fff', color: activeClientFilter === c.id ? '#fff' : 'var(--text-secondary)', borderColor: activeClientFilter === c.id ? 'var(--navy-mid)' : 'var(--border)' }}>
                {c.name} {done}/{count}
              </button>
            )
          })}
          {internalTasks.length > 0 && (
            <button onClick={() => setActiveClientFilter('internal')}
              style={{ padding: '4px 12px', borderRadius: 20, fontSize: 12, cursor: 'pointer', border: '1.5px solid', fontWeight: activeClientFilter === 'internal' ? 700 : 400, background: activeClientFilter === 'internal' ? 'var(--navy-mid)' : '#fff', color: activeClientFilter === 'internal' ? '#fff' : 'var(--text-secondary)', borderColor: activeClientFilter === 'internal' ? 'var(--navy-mid)' : 'var(--border)' }}>
              🏢 Internal ({internalTasks.length})
            </button>
          )}
        </div>
      )}

      {/* Cross-client matrix */}
      {activeClientFilter === null && <BulkStatusMatrix tasks={tasks} clients={clients} projectClientIds={project.client_ids || []} />}

      {/* Phases */}
      {phases.map(phase => {
        const phaseTasks = filteredTasks.filter(t => t.project_phase_id === phase.id)
        const phaseDone = phaseTasks.filter(t => DONE_STATUSES.includes(t.status)).length
        const isExpanded = expandedPhases[phase.id] !== false

        const byWeek = {}
        phaseTasks.forEach(t => {
          const w = t.week_number ? `Week ${t.week_number}` : 'General'
          if (!byWeek[w]) byWeek[w] = []
          byWeek[w].push(t)
        })

        return (
          <div key={phase.id} className="card" style={{ marginBottom: 14, overflow: 'hidden' }}>
            <div className="card-header" style={{ background: 'var(--navy)', cursor: 'pointer' }}
              onClick={() => setExpandedPhases(e => ({ ...e, [phase.id]: !e[phase.id] }))}>
              <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
                {isExpanded ? <ChevronDown size={15} color="rgba(255,255,255,0.6)" /> : <ChevronRight size={15} color="rgba(255,255,255,0.6)" />}
                <span style={{ fontFamily: 'var(--font-display)', fontSize: 15, color: '#fff' }}>{phase.name}</span>
                <span style={{ fontSize: 12, color: 'rgba(255,255,255,0.5)' }}>{phaseDone}/{phaseTasks.length} done</span>
              </div>
              <button className="btn btn-sm" style={{ background: 'rgba(255,255,255,0.15)', color: '#fff', border: 'none' }}
                onClick={e => { e.stopPropagation(); setAddTaskPhase(phase); setShowAddTask(true) }}>
                <Plus size={13} /> Add Task
              </button>
            </div>
            {isExpanded && (
              <div>
                {Object.entries(byWeek).sort(([a], [b]) => {
                  const na = parseInt(a.replace('Week ', '')) || 999
                  const nb = parseInt(b.replace('Week ', '')) || 999
                  return na - nb
                }).map(([week, weekTasks]) => (
                  <div key={week}>
                    <div style={{ padding: '6px 16px', background: 'var(--cream)', fontSize: 11, fontWeight: 700, textTransform: 'uppercase', letterSpacing: '0.8px', color: 'var(--text-muted)', borderBottom: '1px solid var(--border-light)' }}>
                      {week}
                    </div>
                    {renderTaskTable(weekTasks)}
                  </div>
                ))}
                {phaseTasks.length === 0 && <div style={{ padding: '14px 20px', color: 'var(--text-muted)', fontSize: 13, fontStyle: 'italic' }}>No tasks in this phase</div>}
              </div>
            )}
          </div>
        )
      })}

      {/* Unphased tasks */}
      {unassignedTasks.length > 0 && (
        <div className="card" style={{ overflow: 'hidden', marginBottom: 14 }}>
          <div className="card-header" style={{ background: 'var(--cream-dark)' }}>
            <span style={{ fontWeight: 600, fontSize: 13.5, color: 'var(--text-secondary)' }}>No Phase</span>
          </div>
          {renderTaskTable(unassignedTasks)}
        </div>
      )}

      <button className="btn btn-ghost" style={{ marginBottom: 20 }} onClick={() => setShowAddPhase(true)}>
        <Plus size={14} /> Add Phase
      </button>

      {showAddPhase && <AddPhaseModal projectId={project.id} existingCount={phases.length} onClose={() => setShowAddPhase(false)} onSaved={() => { setShowAddPhase(false); load() }} />}
      {showAddTask && <AddTaskModal project={project} phase={addTaskPhase} phases={phases} clients={clients} profiles={profiles} currentUser={currentUser} onClose={() => setShowAddTask(false)} onSaved={() => { setShowAddTask(false); load() }} />}
      {showEditProject && <ProjectModal project={project} clients={clients} profiles={profiles} currentUser={currentUser} onClose={() => setShowEditProject(false)} onSaved={() => { setShowEditProject(false); onEdited() }} />}

    </div>
  )
}

// ─── Project Card ─────────────────────────────────────────────────────────────
function ProjectCard({ project, clients, profiles, onClick }) {
  const ps = getProjectStatus(project.status)
  const projectClients = (project.client_ids || []).map(id => clients.find(c => c.id === id)).filter(Boolean)
  const owner = profiles.find(p => p.id === project.owner_id)
  const daysSinceCheckin = project.last_checked_in_at ? differenceInDays(new Date(), new Date(project.last_checked_in_at)) : null
  const needsCheckin = project.checkin_interval_days > 0 && project.status === 'active' && (daysSinceCheckin === null || daysSinceCheckin >= project.checkin_interval_days)

  return (
    <div className="card" style={{ cursor: 'pointer', transition: 'box-shadow 0.2s' }} onClick={onClick}
      onMouseEnter={e => e.currentTarget.style.boxShadow = 'var(--shadow-md)'}
      onMouseLeave={e => e.currentTarget.style.boxShadow = 'var(--shadow-sm)'}>
      <div style={{ padding: '16px 20px' }}>
        <div style={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', gap: 12 }}>
          <div style={{ flex: 1 }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 6, flexWrap: 'wrap' }}>
              <h3 style={{ fontFamily: 'var(--font-display)', fontSize: 17, color: 'var(--navy)' }}>{project.name}</h3>
              {ps && <span className="status-pill" style={{ background: ps.bg, color: ps.color, fontSize: 11 }}>{ps.icon} {ps.label}</span>}
              {needsCheckin && <span style={{ fontSize: 11, background: '#fdf6e3', color: 'var(--gold)', padding: '2px 8px', borderRadius: 10, fontWeight: 600 }}>⏰ Check-in due</span>}
            </div>
            {project.description && <p style={{ fontSize: 13, color: 'var(--text-secondary)', marginBottom: 8 }}>{project.description}</p>}
            <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap' }}>
              {projectClients.map(c => <span key={c.id} className="badge" style={{ background: 'var(--navy-mid)', color: '#fff', fontSize: 11 }}>{c.name}</span>)}
              {projectClients.length === 0 && <span style={{ fontSize: 12, color: 'var(--text-muted)', fontStyle: 'italic' }}>No clients assigned</span>}
            </div>
          </div>
          <div style={{ textAlign: 'right', flexShrink: 0 }}>
            {owner && <div style={{ fontSize: 12, color: 'var(--text-muted)' }}>Owner: {owner.full_name}</div>}
            <ChevronRight size={18} style={{ color: 'var(--text-muted)', marginTop: 4 }} />
          </div>
        </div>
      </div>
    </div>
  )
}

// ─── Main Projects View ───────────────────────────────────────────────────────
export default function ProjectsView({ clients, profiles, currentUser, profile, selectedProjectId, onProjectsLoaded, onTaskClick, onOpenSeoProject }) {
  const [projects, setProjects] = useState([])
  const [selectedProject, setSelectedProject] = useState(null)
  const [showNewProject, setShowNewProject] = useState(false)
  const [showArchived, setShowArchived] = useState(false)
  const [showQ2Grid, setShowQ2Grid] = useState(false)
  const [loading, setLoading] = useState(true)
  const canCreate = ['supervising_ad', 'ad'].includes(profile?.role)

  const load = useCallback(async () => {
    setLoading(true)
    const { data } = await supabase.from('projects').select('*').order('created_at', { ascending: false })
    setProjects(data || [])
    onProjectsLoaded(data || [])
    setLoading(false)
  }, [onProjectsLoaded])

  useEffect(() => { load() }, [load])

  useEffect(() => {
    if (selectedProjectId && projects.length > 0) {
      const p = projects.find(p => p.id === selectedProjectId)
      if (p) setSelectedProject(p)
    }
  }, [selectedProjectId, projects])

  if (showQ2Grid) {
    return <Q2GridView clients={clients} profiles={profiles} onBack={() => setShowQ2Grid(false)} onOpenSeoProject={onOpenSeoProject} />
  }

  if (selectedProject) {
    return <ProjectDetail project={selectedProject} clients={clients} profiles={profiles} currentUser={currentUser} onBack={() => { setSelectedProject(null); load() }} onEdited={load} onTaskClick={onTaskClick} />
  }

  const active = projects.filter(p => p.status !== 'archived')
  const archived = projects.filter(p => p.status === 'archived')

  return (
    <div>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 20 }}>
        <div style={{ fontSize: 13, color: 'var(--text-muted)' }}>{active.length} active project{active.length !== 1 ? 's' : ''}</div>
        {canCreate && <button className="btn btn-gold" onClick={() => setShowNewProject(true)}><Plus size={15} /> New Project</button>}
      </div>

      {/* Q2 SEO grid card — pinned at the top */}
      <div style={{ marginBottom: 12 }}>
        <Q2GridCard onOpen={() => setShowQ2Grid(true)} />
      </div>

      {loading ? <div style={{ textAlign: 'center', padding: 40, color: 'var(--text-muted)' }}>Loading...</div>
        : active.length === 0 ? (
          <div className="empty-state">
            <Layers size={48} className="empty-state-icon" />
            <h3>No active projects</h3>
            <p>{canCreate ? 'Create a project to coordinate work across multiple clients' : 'No projects yet'}</p>
          </div>
        ) : (
          <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
            {active.map(p => <ProjectCard key={p.id} project={p} clients={clients} profiles={profiles} onClick={() => setSelectedProject(p)} />)}
          </div>
        )
      }

      {archived.length > 0 && (
        <div style={{ marginTop: 32 }}>
          <button className="btn btn-ghost btn-sm" onClick={() => setShowArchived(s => !s)} style={{ marginBottom: 12 }}>
            {showArchived ? <ChevronDown size={13} /> : <ChevronRight size={13} />} {archived.length} Archived
          </button>
          {showArchived && (
            <div style={{ display: 'flex', flexDirection: 'column', gap: 10, opacity: 0.7 }}>
              {archived.map(p => <ProjectCard key={p.id} project={p} clients={clients} profiles={profiles} onClick={() => setSelectedProject(p)} />)}
            </div>
          )}
        </div>
      )}

      {showNewProject && <ProjectModal clients={clients} profiles={profiles} currentUser={currentUser} onClose={() => setShowNewProject(false)} onSaved={() => { setShowNewProject(false); load() }} />}
    </div>
  )
}